import React, { useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { RagBadge } from "@/components/common/RagBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PERIOD_STATE, VERIFY_STATE } from "@/lib/workspace";
import { fmtValue } from "@/lib/format";
import { toast } from "sonner";

function PeriodTile({ p, unit, kpiId, canVerify, canRequest, onChanged }) {
  const st = PERIOD_STATE[p.state] || PERIOD_STATE.unknown;
  const v = p.verification_status && VERIFY_STATE[p.verification_status];
  const [note, setNote] = useState("");
  const [querying, setQuerying] = useState(false);
  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); setQuerying(false); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className={`rounded-lg border p-2.5 ${p.state === "missing" ? "border-[#C95555]/40 bg-[#C95555]/[0.04]" : "border-[#E4E8EF] bg-white"}`} data-testid={`period-${kpiId}-${p.period_key}`}>
      <div className="text-[11px] text-[#1E2D44]/50">{p.label}</div>
      <div className="mt-0.5 font-display text-lg font-bold text-[#0B1729]" data-testid="period-value">
        {p.value == null ? <span className="text-sm font-medium text-[#1E2D44]/40">Not reported</span> : fmtValue(p.value, unit)}
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        <RagBadge rag={st.rag} className="!px-1.5 !py-0 !text-[10px]">{st.label}</RagBadge>
        {v && <RagBadge rag={v.rag} dot={false} className="!px-1.5 !py-0 !text-[10px]">{v.label}</RagBadge>}
      </div>
      {p.target != null && <div className="mt-1 text-[10px] text-[#1E2D44]/40">Target {fmtValue(p.target, unit)}</div>}
      {p.query_note && <div className="mt-1 text-[10px] text-[#C95555]">“{p.query_note}”</div>}
      {canVerify && p.verification_status === "unverified" && (
        <div className="mt-2 flex gap-1">
          <Button size="sm" className="h-7 flex-1 bg-[#3D8B65] px-2 text-[11px] text-white hover:bg-[#2E6B4E]" data-testid={`verify-${kpiId}-${p.period_key}`}
            onClick={() => act(() => api.post(`/workspace/kpis/${kpiId}/verify`, { period_key: p.period_key, decision: "verify" }), "Result verified")}>Verify</Button>
          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => setQuerying(!querying)} data-testid={`query-${kpiId}-${p.period_key}`}>Query</Button>
        </div>
      )}
      {querying && (
        <div className="mt-2 space-y-1">
          <Input value={note} onChange={(e) => setNote(e.target.value)} className="h-8 text-xs" placeholder="What needs checking?" data-testid="query-note" />
          <Button size="sm" className="h-7 w-full text-[11px]" disabled={!note.trim()} data-testid="query-confirm"
            onClick={() => act(() => api.post(`/workspace/kpis/${kpiId}/verify`, { period_key: p.period_key, decision: "query", note }), "Result queried")}>Send query</Button>
        </div>
      )}
      {canRequest && ["missing", "due"].includes(p.state) && (
        <button className="mt-2 text-[11px] font-medium text-[#B87F1E] hover:underline" data-testid={`request-${kpiId}-${p.period_key}`}
          onClick={() => act(() => api.post(`/workspace/kpis/${kpiId}/request-update`, { period_key: p.period_key }), "Update requested")}>
          Request update
        </button>
      )}
    </div>
  );
}

export function KpiPeriodCard({ kpi, canRecord, canVerify = false, canRequest = false, meId, onChanged }) {
  const ownKpi = kpi.owner_id === meId;
  const open = kpi.periods.filter((p) => p.state === "missing" || p.state === "due" || p.verification_status === "queried");
  const [period, setPeriod] = useState(open[open.length - 1]?.period_key || kpi.periods[kpi.periods.length - 1]?.period_key);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (value === "" || isNaN(Number(value))) return toast.error("Enter a number");
    setSaving(true);
    try {
      await api.post(`/workspace/kpis/${kpi.id}/results`, { period_key: period, value: Number(value) });
      toast.success("Result recorded — sent for checking");
      setValue(""); onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="amden-card p-5" data-testid={`kpi-card-${kpi.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-display text-base font-semibold text-[#0B1729]">{kpi.name}</div>
          <div className="mt-0.5 text-xs text-[#1E2D44]/50">
            {kpi.frequency} · {kpi.unit} · target {fmtValue(kpi.target, kpi.unit)} ({kpi.direction === "down" ? "lower is better" : "higher is better"}){kpi.owner_name ? ` · ${kpi.owner_name}` : ""}
          </div>
        </div>
        {kpi.missing > 0 && <RagBadge rag="red" testId={`kpi-missing-${kpi.id}`}>{kpi.missing} missed</RagBadge>}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {kpi.periods.map((p) => (
          <PeriodTile key={p.period_key} p={p} unit={kpi.unit} kpiId={kpi.id} onChanged={onChanged}
            canVerify={canVerify && !ownKpi && p.entered_by !== meId} canRequest={canRequest && !ownKpi} />
        ))}
      </div>
      {kpi.rollup && <RollupStrip rollup={kpi.rollup} unit={kpi.unit} />}
      {canRecord && (
        <div className="mt-4 flex flex-col gap-2 rounded-lg bg-[#F7F9FC] p-3 sm:flex-row sm:items-center">
          <span className="text-xs font-medium text-[#1E2D44]/70">Record a result</span>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-9 bg-white sm:w-56" data-testid={`kpi-period-select-${kpi.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{kpi.periods.map((p) => <SelectItem key={p.period_key} value={p.period_key}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" step="any" value={value} onChange={(e) => setValue(e.target.value)} className="h-9 bg-white sm:w-32" placeholder="Actual" data-testid={`kpi-value-input-${kpi.id}`} />
          <Button onClick={save} disabled={saving} className="h-9 bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid={`kpi-save-${kpi.id}`}>
            {saving ? <Icons.Loader2 className="animate-spin" size={15} /> : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
}

function RollupStrip({ rollup, unit }) {
  if (!rollup.valid) return <p className="mt-3 text-xs text-[#B87F1E]">Team roll-up unavailable: {rollup.reason}</p>;
  return (
    <div className="mt-4 rounded-lg border border-dashed border-[#D2D9E3] p-3" data-testid="kpi-rollup">
      <div className="mb-2 text-xs font-medium text-[#1E2D44]/70">
        Team roll-up ({rollup.aggregation}) of {rollup.children.map((c) => c.name).join(", ")}
      </div>
      <div className="flex flex-wrap gap-2">
        {rollup.periods.map((p) => (
          <div key={p.period_key} className="rounded-md bg-white px-2.5 py-1.5 text-xs" data-testid={`rollup-${p.period_key}`}>
            <div className="text-[10px] text-[#1E2D44]/45">{p.label}</div>
            {p.value == null
              ? <div className="font-medium text-[#1E2D44]/50">Incomplete · {p.reported}/{p.expected} reported</div>
              : <div className="font-semibold text-[#0B1729]">{fmtValue(p.value, unit)}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
